/**
 * Backtest Detail Route
 * View backtest results in detail
 */

import { createFileRoute, useNavigate, Link } from '@tanstack/react-router'
import { useAppStore } from '@/lib/store'
import { BacktestResults } from '@/components/backtesting/backtest-results'
import { ArrowLeft, Download, Share2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/trading/badge'

export const Route = createFileRoute('/dashboard/backtests/$id')({
  component: BacktestDetail,
})

function BacktestDetail() {
  const { id } = Route.useParams()
  const navigate = useNavigate()
  const { state } = useAppStore()

  const backtest = state.backtests.find((bt) => bt.id === id)

  if (!backtest) {
    return (
      <div className="p-8 text-center">
        <h2 className="text-xl font-semibold mb-2">Backtest Not Found</h2>
        <p className="text-muted-foreground mb-4">The requested backtest could not be found.</p>
        <Link to="/dashboard/backtests">
          <Button>Back to Backtests</Button>
        </Link>
      </div>
    )
  }

  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/dashboard/backtests">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold">{backtest.name}</h1>
            <p className="text-muted-foreground text-sm mt-1">
              {backtest.configuration.symbol} • {new Date(backtest.date).toLocaleDateString()}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm">
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
          <Button variant="outline" size="sm">
            <Share2 className="h-4 w-4 mr-2" />
            Share
          </Button>
        </div>
      </div>

      {/* Status Badge */}
      {backtest.status === 'running' && (
        <div className="flex items-center gap-2">
          <div className="h-2 w-2 rounded-full bg-primary animate-pulse" />
          <Badge variant="info">Running</Badge>
        </div>
      )}

      {/* Results */}
      {backtest.results ? (
        <BacktestResults results={backtest.results} configuration={backtest.configuration} />
      ) : (
        <div className="p-12 text-center border border-border rounded-xl bg-card">
          <p className="text-muted-foreground">
            {backtest.status === 'running'
              ? 'Backtest is currently running. Results will appear here when complete.'
              : 'No results available.'}
          </p>
        </div>
      )}
    </div>
  )
}
